/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ApexCharts from "apexcharts";
import {
  Button, Grid, Form, Divider, Checkbox,
  Dropdown, Segment, Dimmer, Loader
} from "semantic-ui-react";
import MonitoringReport from "components/login-config/configuration/report/MonitoringReport";
import AnalyzerDPReport from "components/login-config/configuration/report/AnalyzerDPReport";
import FullReport from "components/login-config/configuration/report/FullReport";
import Constants from "utilities/Constants";
import Strings from "utilities/StringSelector";
import Utils from "utilities/Utils";

const UtilsInstance = new Utils();

export const getDefaultFiber = () => ({
  FB1: {
    channel2: 0,
    channel3: 0,
    signal: false,
    statusChannel2: "",
    statusChannel3: ""
  }
});

export const getDefaultFiberChannelActive = () => ({
  FB1: {
    channel2Active: false,
    channel3Active: false,
    signalActive: false
  }
});

export const normalizeFiber = (fiber) => ({
  FB1: {
    ...getDefaultFiber().FB1,
    ...(fiber && typeof fiber.FB1 === "object" ? fiber.FB1 : {})
  }
});

export const normalizeFiberChannelActive = (fiberChannelActive) => ({
  FB1: {
    ...getDefaultFiberChannelActive().FB1,
    ...(fiberChannelActive && typeof fiberChannelActive.FB1 === "object" ? fiberChannelActive.FB1 : {})
  }
});

const dropdownOptions = [
  {
    key: Constants.CONFORM,
    value: Constants.CONFORM,
    text: Strings.CONFORM_STRING
  },
  {
    key: Constants.CONFORM_W_RESTRICTION,
    value: Constants.CONFORM_W_RESTRICTION,
    text: Strings.CONFORM_W_RESTRICTION_STRING
  },
  {
    key: Constants.NON_CONFORM,
    value: Constants.NON_CONFORM,
    text: Strings.NON_CONFORM_STRING
  }
];

let dpBarGraphOptions = {
  tooltip: {
    enabled: true,
    x: {
      show: true,
      formatter: (val) => {
        return Strings.ADDRESS + ": " + val;
      },
    },
  },
  chart: {
    id: Constants.AMPLITUDE_ID,
    type: "bar",
    locales: [{
      "name": "pt-br",
      "options": {
        "toolbar": {
          "exportToSVG": "Baixar SVG",
          "exportToPNG": "Baixar PNG",
          "exportToCSV": "Baixar CSV",
          "menu": "Baixar"
        }
      }
    }],
    defaultLocale: "pt-br",
  },
  title: {
    text: Strings.AMPLITUDE,
    align: "center"
  },
  xaxis: {
    type: "category",
    title: {
      text: Strings.ADDRESS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        fontSize: Constants.LABELS_FONT_SIZE
      }
    }
  },
  yaxis: {
    min: 0,
    max: 12,
    tickAmount: 12,
    labels: {
      show: true,
      formatter: function (val) {
        const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

        if (lang !== Constants.EN_LANG_KEY) {
          return UtilsInstance.replacePointWithComma(val.toFixed(2)) + Strings.VOLTAGE_PEAK_TO_PEAK;
        }
        else {
          return val.toFixed(2) + Strings.VOLTAGE_PEAK_TO_PEAK;
        }
      },
      style: {
        fontSize: Constants.LABELS_FONT_SIZE
      }
    },
    title: {
      text: Strings.VOLTAGE_V_PEAK_TO_PEAK
    },
  },
  series: [{
    name: Strings.AMPLITUDE,
    data: [],
  }],
  colors: [function ({ value }) {
    return (UtilsInstance.getBarGraphColorBasedOnValue(value, Constants));
  }],
  plotOptions: {
    bar: {
      dataLabels: {
        position: "top",
      }
    }
  },
  dataLabels: {
    formatter: function (val) {
      const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

      if (val < 0) {
        // Don't show the number above a bar with
        // negative values
        return "";
      }
      else if (val === 0) {
        return (lang !== Constants.EN_LANG_KEY) ? "0,00" : "0.00";
      }
      else {
        return (lang !== Constants.EN_LANG_KEY) ? UtilsInstance.replacePointWithComma(val.toString()) : val.toString();
      }
    },
    style: {
      fontSize: "16px",
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: 400,
      colors: ["#212121"],
    },
    offsetY: -25,
  },
};

let dpWaveFormOptions = {
  series: [{ data: [] }],
  chart: {
    id: Constants.CHART_ID,
    type: "line",
  },
  colors: [Constants.PURPLE_HEX],
  markers: {
    size: 0
  },
  dataLabels: {
    enabled: false
  },
  title: {
    text: Strings.WAVE_FORM,
    align: "center",
    style: {
      fontSize: "14px",
      fontFamily: "Helvetica, Arial, sans-serif"
    }
  },
  stroke: {
    curve: "straight",
    width: 2
  },
  xaxis: {
    type: "numeric",
    title: {
      text: Strings.TIME_NS
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      formatter: function (val) {
        const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

        if (lang !== Constants.EN_LANG_KEY) {
          return UtilsInstance.replacePointWithComma(val.toFixed(1));
        }
        else {
          return val.toFixed(1);
        }
      },
      style: {
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
        cssClass: "apexcharts-xaxis-label",
      }
    }
  },
  yaxis: {
    min: -8,
    max: 8,
    tickAmount: 16,
    labels: {
      show: true,
      formatter: function (val) {
        const lang = localStorage.getItem(Constants.LOCAL_STORAGE_KEY);

        if (lang !== Constants.EN_LANG_KEY) {
          return UtilsInstance.replacePointWithComma(val.toFixed(2)) + Strings.VOLTAGE_PEAK_TO_PEAK;
        }
        else {
          return val.toFixed(2) + Strings.VOLTAGE_PEAK_TO_PEAK;
        }
      },
      style: {
        colors: [],
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-yaxis-label",
      }
    },
    title: {
      text: Strings.VOLTAGE_V
    },
  },
  annotations: {
    position: "front",
    xaxis: []
  }
};

class Report extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isGenerateReportDimmerActive: false,

      savedClients: [],
      savedExecutors: [],

      isMonChecked: false,
      isDPChecked: false,

      // User input data
      clientProjectName: "",
      clientNetworkName: "",
      clientLocation: "",
      clientCompanyName: "",
      clientAccountableName: "",
      clientAddress: "",
      clientCity: "",
      clientTel: "",
      executingCompanyName: "",
      executingAccountableName: "",
      executingTel: "",
      executingEmail: "",
      notes: "",
      conformity: "",
      // The report
      report: "",
      reportFilename: "",
      // Report loading
      startSaveTime: "",
      loaderText1: Strings.GENERATING_REPORT_WAITING,
      loaderText2: ""
    };
  }

  setGeneratingDimmerState = (isActive) => {
    if (!isActive) {
      this.setState({ isGenerateReportDimmerActive: isActive }, () => {
        alert(Strings.GENERATING_REPORT_SUCCESS);
      });
    }
    else {
      this.setState({ isGenerateReportDimmerActive: isActive });
    }
  }

  // Verifies if the user has the credentials to be logged in
  // If he does he is re-directed to the configuration page
  // If he doesn't he is re-directed to the login page
  isUserLoggedIn = () => {
    if (sessionStorage.auth !== Constants.AUTH_STR) {
      this.props.history.push({ pathname: "/login" });
    }
  }

  // ----------------- Data retrieval functions ----------------
  retrieveMonitoringData = async (userInput) => {
    this.setGeneratingDimmerState(true);

    let livelist = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS, { mode: "cors" })
      .then(res => res.json())
      .then(data => data.sort(UtilsInstance.sortBy("address", false, parseInt)));

    let log = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "log", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);

    let uptime = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "homePage", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.uptime);

    let deviceStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "deviceStatistics", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.deviceStatistics);

    let networkStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "networkStatistics", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.networkStatistics);

    let trafficStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "trafficStatistics", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);

    let configData = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "config", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);

    let wifiStatus = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "wifiStatus", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.status);

    let fiber;
    let fiberChannelActive;
    
    if (sessionStorage.getItem("isFiberProcessOn") === "true") {
      fiber = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "fiber", { mode: "cors" })
        .then(UtilsInstance.checkStatus)  
        .then(res => res.json())
        .then(normalizeFiber)
        .catch((e) => {
          return getDefaultFiber();
        });

      fiberChannelActive = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "fiber/fiberChannelActive", { mode: "cors" })
        .then(UtilsInstance.checkStatus)
        .then(res => res.json())
        .then(normalizeFiberChannelActive)
        .catch((e) => {
          return getDefaultFiberChannelActive();
        });
    } else {
      fiber = getDefaultFiber();
      fiberChannelActive = getDefaultFiberChannelActive();
    }

    let wifiConfigData = "";

    if (wifiStatus === Constants.CONNECTED_STATUS) {
      wifiConfigData = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "wifiConfig", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);
    }
    else {
      wifiConfigData = {
        "wifiMode": "",
        "hiddenSSID": false,
        "wifiName": "",
        "wifiAddr": "",
        "wifiNetmask": "",
        "wifiGateway": "",
        "wifiDNS": "",
        "wifiPwd": ""
      };
    }

    let tag = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "tag", { mode: "cors" })
      .then(res => res.json())
      .then(data => data.tag);

    let graphicNetworkStatus = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "graphicNetworkStatus", { mode: "cors" })
      .then(res => res.json())
      .then(data => data);

    let availability = graphicNetworkStatus.percentageAvailable + graphicNetworkStatus.percentageDiagnostic;
    availability = availability.toFixed(0).concat("%");

    let ip = configData.addr;

    this.setState({
      reportFilename: `${Strings.REPORT_MN_PB_PRO_PROTOCOL} (${ip}).pdf`,
      report: <MonitoringReport
        userInput={userInput}
        livelist={livelist}
        log={log}
        uptime={uptime}
        deviceStatistics={deviceStatistics}
        networkStatistics={networkStatistics}
        trafficStatistics={trafficStatistics}
        configData={configData}
        tag={tag}
        wifiConfigData={wifiConfigData}
        availability={availability}
        conformity={this.state.conformity}
        fiber={fiber}
        fiberChannelActive={fiberChannelActive}
      />
    });
  }

  retrieveAnalyzerDPData = async (userInput) => {
    this.setGeneratingDimmerState(true);

    let allTimesOk = false;
    let oscilloscopeData = [];
    let showMoreThanOneMinuteWaiting = true;
    let showMoreThanTwoMinutesWaiting = true;

    const startCounterTime = Date.now();

    while (allTimesOk === false) {
      oscilloscopeData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "oscilloscopeWaveAll1byte", { mode: "cors" })
        .then(res => res.json())
        .then(data => UtilsInstance.excludeEmptyWaves(data).sort(UtilsInstance.sortBy("address", false, parseInt)));

      let timeNow = Date.now();
      let elapsedTimeSinceGenerateReportClick = timeNow - this.state.startSaveTime;
      let hasOldEvent = false;

      console.log("Tempo desde que clicou no botão: ", elapsedTimeSinceGenerateReportClick);

      for (let i = 0; i < oscilloscopeData.length; i++) {
        let waveTime = oscilloscopeData[i].time * 1000;
        console.log("Tempo da forma de onda: ", waveTime);
        if (waveTime > elapsedTimeSinceGenerateReportClick) {
          console.log("Tempo da forma de onda muito antigo");
          hasOldEvent = true;
          break;
        }
      }

      const currentTime = Date.now();
      console.log("\nTempo decorrido: ", ((currentTime - startCounterTime) / 1000), " segundos\n");

      if ((currentTime - startCounterTime >= 60000) && (currentTime - startCounterTime < 120000) && showMoreThanOneMinuteWaiting) {
        showMoreThanOneMinuteWaiting = false;
        this.setState({
          loaderText1: Strings.GENERATING_REPORT_WAITING,
          loaderText2: Strings.GENERATING_REPORT_WAITING_1
        }, () => {
          console.log("Primeiro minuto");
        });
      }
      else if ((currentTime - startCounterTime >= 120000) && showMoreThanTwoMinutesWaiting) {
        showMoreThanTwoMinutesWaiting = false;
        this.setState({
          loaderText1: Strings.GENERATING_REPORT_WAITING,
          loaderText2: Strings.GENERATING_REPORT_WAITING_2
        }, () => {
          console.log("Segundo minuto");
        });
      }

      if (!hasOldEvent || (currentTime - startCounterTime >= 180000)) {
        allTimesOk = true;
      }
    }

    // Se todas as formas de onda forem recentes, ou se houver passado 3 minutos, vamos para
    // a próxima parte da geração do relatório
    if (allTimesOk) {
      let monitorIP = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "monitorIP", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.monitorAddr);

      let liveListMonitor = [];

      if (monitorIP !== "") {
        liveListMonitor = await new Promise((resolve, reject) => {
          let didTimeOut = false;

          const timeout = setTimeout(() => {
            didTimeOut = true;
            reject(new Error("Request timed out!"));
          }, 200);

          return fetch("http://" + monitorIP + ":5000/", { mode: "cors" })
            .then((res) => {
              clearTimeout(timeout);
              resolve(res);
            })
            .catch((error) => {
              // Rejection already happened with setTimeout
              if (didTimeOut) return;
              // Reject with error
              reject(error);
            });
        })
          .then(res => res.json())
          .then(data => data)
          .catch(() => {
            return [];
          });
      }

      let liveList = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => data.sort(UtilsInstance.sortBy("address", false, function (a) { return a; })));

      liveList = UtilsInstance.addTagToDevices(liveList, liveListMonitor);
      let livelistMap = this.createLivelistMap(liveList);

      let networkData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "networkData", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let uptime = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "homePage", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.uptime);

      let tag = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "tag", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getOscilloscopeImgURIs(oscilloscopeData, livelistMap);

      let ampImgURIs = await this.getDPBarGraphImgURIsAmp(liveList);
      let minAmpImgURIs = await this.getDPBarGraphImgURIsMinAmp(liveList);

      let logData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "log", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      let configData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "config", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let wifiStatus = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "wifiStatus", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.status);

      let wifiConfigData = "";

      if (wifiStatus === Constants.CONNECTED_STATUS) {
        wifiConfigData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "wifiConfig", { mode: "cors" })
          .then(res => res.json())
          .then(data => data);
      }
      else {
        wifiConfigData = {
          "wifiMode": "",
          "hiddenSSID": false,
          "wifiName": "",
          "wifiAddr": "",
          "wifiNetmask": "",
          "wifiGateway": "",
          "wifiDNS": "",
          "wifiPwd": ""
        };
      }

      let ip = configData.addr;

      this.setState({
        reportFilename: `${Strings.REPORT_MN_PB_PRO_PHYS_MEDIUM} (${ip}).pdf`,
        report: <AnalyzerDPReport
          userInput={userInput}
          livelist={liveList}
          livelistMap={livelistMap}
          baudRate={networkData.baudrate}
          masters={networkData.nMasters}
          slaves={networkData.nSlaves}
          tensaoIdle={networkData.tensaoIdle}
          tag={tag}
          uptime={uptime}
          imgURIs={imgURIs}
          ampImgURIs={ampImgURIs}
          minAmpImgURIs={minAmpImgURIs}
          log={logData}
          configData={configData}
          wifiConfigData={wifiConfigData}
          conformity={this.state.conformity}
        />
      });
    }
  }

  retrieveAnalyzerDPAndMonitoringData = async (userInput) => {
    this.setGeneratingDimmerState(true);

    let allTimesOk = false;
    let oscilloscopeData = [];
    let showMoreThanOneMinuteWaiting = true;
    let showMoreThanTwoMinutesWaiting = true;

    const startCounterTime = Date.now();

    while (allTimesOk === false) {
      oscilloscopeData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "oscilloscopeWaveAll1byte", { mode: "cors" })
        .then(res => res.json())
        .then(data => UtilsInstance.excludeEmptyWaves(data).sort(UtilsInstance.sortBy("address", false, parseInt)));

      let timeNow = Date.now();
      let elapsedTimeSinceGenerateReportClick = timeNow - this.state.startSaveTime;
      let hasOldEvent = false;

      console.log("Tempo desde que clicou no botão: ", elapsedTimeSinceGenerateReportClick);

      for (let i = 0; i < oscilloscopeData.length; i++) {
        let waveTime = oscilloscopeData[i].time * 1000;
        console.log("Tempo da forma de onda: ", waveTime);
        if (waveTime > elapsedTimeSinceGenerateReportClick) {
          console.log("Tempo da forma de onda muito antigo");
          hasOldEvent = true;
          break;
        }
      }

      const currentTime = Date.now();
      console.log("\nTempo decorrido: ", ((currentTime - startCounterTime) / 1000), " segundos\n");

      if ((currentTime - startCounterTime >= 60000) && (currentTime - startCounterTime < 120000) && showMoreThanOneMinuteWaiting) {
        showMoreThanOneMinuteWaiting = false;
        this.setState({
          loaderText1: Strings.GENERATING_REPORT_WAITING,
          loaderText2: Strings.GENERATING_REPORT_WAITING_1
        }, () => {
          console.log("Primeiro minuto");
        });
      }
      else if ((currentTime - startCounterTime >= 120000) && showMoreThanTwoMinutesWaiting) {
        showMoreThanTwoMinutesWaiting = false;
        this.setState({
          loaderText1: Strings.GENERATING_REPORT_WAITING,
          loaderText2: Strings.GENERATING_REPORT_WAITING_2
        }, () => {
          console.log("Segundo minuto");
        });
      }

      if (!hasOldEvent || (currentTime - startCounterTime >= 180000)) {
        allTimesOk = true;
      }
    }

    // Se todas as formas de onda forem recentes, ou se houver passado 3 minutos, vamos para
    // a próxima parte da geração do relatório
    if (allTimesOk) {
      let monitorIP = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "monitorIP", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.monitorAddr);

      let liveListMonitor = [];

      if (monitorIP !== "") {
        liveListMonitor = await new Promise((resolve, reject) => {
          let didTimeOut = false;

          const timeout = setTimeout(() => {
            didTimeOut = true;
            reject(new Error("Request timed out!"));
          }, 200);

          return fetch("http://" + monitorIP + ":5000/", { mode: "cors" })
            .then((res) => {
              clearTimeout(timeout);
              resolve(res);
            })
            .catch((error) => {
              // Rejection already happened with setTimeout
              if (didTimeOut) return;
              // Reject with error
              reject(error);
            });
        })
          .then(res => res.json())
          .then(data => data)
          .catch(() => {
            return [];
          });
      }

      let scopeLiveList = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => data.sort(UtilsInstance.sortBy("address", false, function (a) { return a; })));

      scopeLiveList = UtilsInstance.addTagToDevices(scopeLiveList, liveListMonitor);
      let livelistMap = this.createLivelistMap(scopeLiveList);

      let networkData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "networkData", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let scopeTag = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "tag", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.tag);

      let imgURIs = await this.getOscilloscopeImgURIs(oscilloscopeData, livelistMap);

      let ampImgURIs = await this.getDPBarGraphImgURIsAmp(scopeLiveList);
      let minAmpImgURIs = await this.getDPBarGraphImgURIsMinAmp(scopeLiveList);

      let scopeLogData = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "log", { mode: "cors" })
        .then(res => res.json())
        .then(data => this.getNewest200Events(data));

      //-----------------------------------------------------------------------------------------
      //------------------------ Parte de monitoramento --------------------------------
      //-----------------------------------------------------------------------------------------
      let monitorLivelist = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS, { mode: "cors" })
        .then(res => res.json())
        .then(data => data.sort(UtilsInstance.sortBy("address", false, parseInt)));

      let monitorLog = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "log", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let monitorUptime = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "homePage", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.uptime);

      let monitorDeviceStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "deviceStatistics", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.deviceStatistics);

      let monitorNetworkStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "networkStatistics", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.networkStatistics);

      let monitorTrafficStatistics = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "trafficStatistics", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let tag = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "tag", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.tag);

      let graphicNetworkStatus = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "graphicNetworkStatus", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let availability = graphicNetworkStatus.percentageAvailable + graphicNetworkStatus.percentageDiagnostic;
      availability = availability.toFixed(0).concat("%");

      //-----------------------------------------------------------------------------------------
      //------------------------------ Parte da Fibra Ótica -------------------------------------
      //-----------------------------------------------------------------------------------------

      let fiber;
      let fiberChannelActive;
      
      if (sessionStorage.getItem("isFiberProcessOn") === "true") {
        fiber = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "fiber", { mode: "cors" })
          .then(UtilsInstance.checkStatus)  
          .then(res => res.json())
          .then(normalizeFiber)
          .catch((e) => {
            return getDefaultFiber();
          });

        fiberChannelActive = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "fiber/fiberChannelActive", { mode: "cors" })
          .then(UtilsInstance.checkStatus)
          .then(res => res.json())
          .then(normalizeFiberChannelActive)
          .catch((e) => {
            return getDefaultFiberChannelActive();
          });
      } else {
        fiber = getDefaultFiber();
        fiberChannelActive = getDefaultFiberChannelActive();
      }
      //-----------------------------------------------------------------------------------------
      //------------------------ Parte das configurações de rede --------------------------------
      //-----------------------------------------------------------------------------------------

      // Informaçções da rede ethernet
      let configData = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "config", { mode: "cors" })
        .then(res => res.json())
        .then(data => data);

      let wifiStatus = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "wifiStatus", { mode: "cors" })
        .then(res => res.json())
        .then(data => data.status);

      // Informações da rede WLAN
      let wifiConfigData = "";

      if (wifiStatus === Constants.CONNECTED_STATUS) {
        wifiConfigData = await fetch(Constants.MONITOR_REQUEST_IP_ADDRESS + "wifiConfig", { mode: "cors" })
          .then(res => res.json())
          .then(data => data);
      }
      else {
        wifiConfigData = {
          "wifiMode": "",
          "hiddenSSID": false,
          "wifiName": "",
          "wifiAddr": "",
          "wifiNetmask": "",
          "wifiGateway": "",
          "wifiDNS": "",
          "wifiPwd": ""
        };
      }

      let ip = configData.addr;

      this.setState({
        reportFilename: `${Strings.REPORT_MN_PB_PRO_FULL} (${ip}).pdf`,
        report: <FullReport
          userInput={userInput}
          // Parte do Monitor
          livelist={monitorLivelist}
          log={monitorLog}
          uptime={monitorUptime}
          deviceStatistics={monitorDeviceStatistics}
          networkStatistics={monitorNetworkStatistics}
          trafficStatistics={monitorTrafficStatistics}
          tag={tag}
          // Fibra Ótica
          fiber={fiber}
          fiberChannelActive={fiberChannelActive}
          // Parte do Scope DP 
          scopeLiveList={scopeLiveList}
          livelistMap={livelistMap}
          baudRate={networkData.baudrate}
          masters={networkData.nMasters}
          slaves={networkData.nSlaves}
          tensaoIdle={networkData.tensaoIdle}
          scopeTag={scopeTag}
          imgURIs={imgURIs}
          ampImgURIs={ampImgURIs}
          minAmpImgURIs={minAmpImgURIs}
          scopeLog={scopeLogData}
          // Informações da rede Ethernet
          configData={configData}
          // Informações da rede WLAN
          wifiConfigData={wifiConfigData}
          // Disponibilidade
          availability={availability}
          // Conformidade
          conformity={this.state.conformity}
        />
      });
    }
  }

  // ----------------- Event Handlers -----------------
  handleChange = (e, { name, value }) => this.setState({ [name]: value })

  handleConformityDropdownItemClick = (event, info) => {
    this.setState({ conformity: info.value });
  }

  handleSubmit = async () => {
    let clientImg = "";
    let executorImg = "";
    let startSaveTime = await this.startSaveWaves();
    console.log("Save time: ", startSaveTime);

    if (document.getElementById("client-img").files[0] !== undefined) {
      clientImg = URL.createObjectURL(document.getElementById("client-img").files[0]);
    }

    if (document.getElementById("executor-img").files[0] !== undefined) {
      executorImg = URL.createObjectURL(document.getElementById("executor-img").files[0]);
    }

    this.setState({
      report: "",
      startSaveTime: startSaveTime
    }, () => {
      let userInput = {
        // Client info
        clientProjectName: this.state.clientProjectName,
        clientNetworkName: this.state.clientNetworkName,
        clientLocation: this.state.clientLocation,
        clientCompanyName: this.state.clientCompanyName,
        clientAccountableName: this.state.clientAccountableName,
        clientAddress: this.state.clientAddress,
        clientCity: this.state.clientCity,
        clientTel: this.state.clientTel,
        clientImg: clientImg,
        // Executing company info
        executingCompanyName: this.state.executingCompanyName,
        executingAccountableName: this.state.executingAccountableName,
        executingTel: this.state.executingTel,
        executingEmail: this.state.executingEmail,
        executorImg: executorImg,
        // Notes
        notes: this.state.notes,
        // Conformity
        conformity: this.state.conformity
      };

      // If none of the checkboxes are selected
      if (!this.state.isMonChecked && !this.state.isDPChecked) {
        alert(Strings.SELECT_REPORT_TYPE);
      }
      // If only the monitoring checkbox is selected
      else if (this.state.isMonChecked && !this.state.isDPChecked) {
        this.retrieveMonitoringData(userInput);
      }
      // If only the analyzer DP checkbox is selected
      else if (!this.state.isMonChecked && this.state.isDPChecked) {
        this.retrieveAnalyzerDPData(userInput);
      }
      // If the monitoring and analyzer DP checkboxes are selected
      else if (this.state.isMonChecked && this.state.isDPChecked) {
        this.retrieveAnalyzerDPAndMonitoringData(userInput);
      }
    });

  }

  toggleDPCheckbox = () => {
    this.setState({ isDPChecked: !this.state.isDPChecked });
  }

  toggleMonitoringCheckbox = () => {
    this.setState({ isMonChecked: !this.state.isMonChecked });
  }

  // ----------------- Generate PDF parts utilities ---------------------
  createLivelistMap = (livelistData) => {
    let livelistMap = new Map();

    for (let i = 0; i < livelistData.length; i++) {
      livelistMap.set(livelistData[i].address, livelistData[i]);
    }

    return livelistMap;
  }

  formatTime = (logEntry, timeNow) => {
    if (logEntry.ntp !== null && logEntry.ntp === true) {
      let parsedMillis = Date.parse(logEntry.time);
      let dateToShow = new Date(parsedMillis);
      return dateToShow.toLocaleString("pt-BR");
    }
    else if (logEntry.time !== undefined && logEntry.time !== null) {
      const eventTimeInMillis = logEntry.time * 1000;
      const time = timeNow - eventTimeInMillis;
      const dateToShow = new Date(time);
      return (dateToShow.toLocaleString("pt-BR"));
    }
    else {
      return Strings.BEFORE_MONITOR_INIT_STR;
    }
  }

  getOscilloscopeImgURIs = async (oscilloscopeData, livelistMap) => {
    let imgURIs = [];
    let timeNow = new Date().getTime();
    document.getElementById("chart").style.display = "block";

    // Render the chart
    let chart = new ApexCharts(document.querySelector("#chart"), dpWaveFormOptions);
    chart.render();

    for (let i = 0; i < oscilloscopeData.length; i++) {
      // Every device will have to render its BA and AB waves
      let imgURIObj = { "address": -1, "time": "", "waveBA": "", "waveAB": "" };
      let options = {};

      if (livelistMap.get(oscilloscopeData[i].address) === undefined) { continue; }

      let hasWaveProblem = livelistMap.get(oscilloscopeData[i].address).hasWaveProblem;
      let subtitleText = "";

      if (hasWaveProblem) {
        subtitleText = Strings.SLAVE_NOT_FOUND_SHOWING_LAST_WAVEFORM;
      }

      // Render the wave B - A to get its imgURI
      options = this.getFormatedDPOptions(oscilloscopeData[i].wave, dpWaveFormOptions);

      options = {
        ...options,
        colors: [Constants.PURPLE_HEX],
        series: [
          { name: Strings.WAVE_B_A, data: this.formatDPWaveTimes(oscilloscopeData[i].wave) }
        ],
        subtitle: {
          text: subtitleText,
          align: "center"
        }
      };

      chart.updateOptions(options, false, false, false);

      imgURIObj.waveBA = await chart.dataURI();

      // Now render the A and B waves and get their imgURIs
      options = {
        ...options,
        colors: [Constants.GREEN_HEX, Constants.RED_HEX],
        series: [
          { name: Strings.WAVE_A, data: this.formatDPWaveTimes(oscilloscopeData[i].waveA) },
          { name: Strings.WAVE_B, data: this.formatDPWaveTimes(oscilloscopeData[i].waveB) }
        ],
        subtitle: {
          text: subtitleText,
          align: "center"
        }
      };

      chart.updateOptions(options, false, false, false);

      imgURIObj.waveAB = await chart.dataURI();

      imgURIObj.address = await oscilloscopeData[i].address;
      imgURIObj.time = await this.formatTime(oscilloscopeData[i], timeNow);

      // Save both waves URIs
      imgURIs.push(imgURIObj);
    }

    document.getElementById("chart").style.display = "none";

    chart.destroy();

    return imgURIs;
  }

  getFormatedDPOptions = (wave, dpOptions) => {
    let options = {};

    if (wave[wave.length - 1][0] < 1000) {
      // Set the time values to nanosseconds
      options = {
        ...dpOptions,
        xaxis: {
          type: "numeric",
          title: {
            text: Strings.TIME_NS
          },
          crosshairs: {
            show: "true"
          }
        }
      };
    }
    else if (wave[wave.length - 1][0] >= 1000
      && wave[wave.length - 1][0] < 1000000) {
      // Set the time values to microsseconds
      options = {
        ...dpOptions,
        xaxis: {
          type: "numeric",
          title: {
            text: Strings.TIME_US
          },
          crosshairs: {
            show: "true"
          }
        }
      };
    }
    else if (wave[wave.length - 1][0] >= 1000000) {
      // Set the time values to millisseconds
      options = {
        ...dpOptions,
        xaxis: {
          type: "numeric",
          title: {
            text: Strings.TIME_MS
          },
          crosshairs: {
            show: "true"
          }
        }
      };
    }

    return options;
  }

  formatDPWaveTimes = (wave) => {
    if (wave[wave.length - 1][0] < 1000) {
      // Set the time values to nanosseconds 
      // (it already is by default, so do nothing here)
    }
    else if (wave[wave.length - 1][0] >= 1000
      && wave[wave.length - 1][0] < 1000000) {
      // Set the time values to microsseconds
      for (let i = 0; i < wave.length; i++) {
        wave[i][0] = wave[i][0] / (10 ** 3);
      }
    }
    else if (wave[wave.length - 1][0] >= 1000000) {
      // Set the time values to millisseconds
      for (let i = 0; i < wave.length; i++) {
        wave[i][0] = wave[i][0] / (10 ** 6);
      }
    }

    return wave;
  }

  getDPBarGraphImgURIsAmp = async (livelist) => {
    // Make a copy of the live list array
    let devicesToShow = livelist.slice();

    let ampImgURIs = [];
    document.getElementById("barchart").style.display = "block";

    let apexChart = new ApexCharts(document.querySelector("#barchart"), dpBarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(devicesToShow.length / Constants.MAX_DEV_PER_PAGE);

    let ampMap = this.createDPAscAddrAmpPageMap(numOfPages, devicesToShow);
    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // First get the imgURIs of the amplitude bar graph
      options = {
        ...dpBarGraphOptions,
        series: [
          { name: Strings.AMPLITUDE, data: ampMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      ampImgURIs.push(imgURIObj);
    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return ampImgURIs;
  }

  getDPBarGraphImgURIsMinAmp = async (livelist) => {
    // Make a copy of the live list array
    let devicesToShow = livelist.slice();

    let minAmpImgURIs = [];
    document.getElementById("barchart").style.display = "block";

    let apexChart = new ApexCharts(document.querySelector("#barchart"), dpBarGraphOptions);
    apexChart.render();

    let numOfPages = Math.ceil(devicesToShow.length / Constants.MAX_DEV_PER_PAGE);

    let minAmpMap = this.createDPAscAddrMinAmpPageMap(numOfPages, devicesToShow);
    let imgURIObj = {};

    for (let i = 0; i < numOfPages; i++) {
      let options = {};

      // Then get the imgURIs of the min amplitude bar graph
      options = {
        ...dpBarGraphOptions,
        series: [
          { name: Strings.MIN_AMPLITUDE, data: minAmpMap.get(i) }
        ]
      };

      apexChart.updateOptions(options, false, false, false);

      imgURIObj = await apexChart.dataURI();

      minAmpImgURIs.push(imgURIObj);
    }

    document.getElementById("barchart").style.display = "none";

    apexChart.destroy();

    return minAmpImgURIs;
  }

  createDPAscAddrAmpPageMap = (numOfPages, livelist) => {
    let ascAddrAmpMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "address",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let ampPageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        ampPageSeries.push({
          x: devicesSlice[j].address.toString(),
          y: UtilsInstance.getFormatedAmplitudeForFaded(
            devicesSlice[j].amplitude,
            devicesSlice[j].hasWaveProblem)
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - ampPageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            ampPageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrAmpMap.set(i, ampPageSeries);
    }

    return ascAddrAmpMap;
  }

  createDPAscAddrMinAmpPageMap = (numOfPages, livelist) => {
    let ascAddrMinAmpMap = new Map();

    let ascAddrSortedDevices = livelist.sort(
      UtilsInstance.sortBy(
        "address",
        false,
        parseInt)
    );

    for (let i = 0; i < numOfPages; i++) {
      let devicesSlice = [];

      if (i === (numOfPages - 1)) {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, ascAddrSortedDevices.length);
      }
      else {
        devicesSlice = ascAddrSortedDevices.slice(i * Constants.MAX_DEV_PER_PAGE, (i + 1) * Constants.MAX_DEV_PER_PAGE);
      }

      let minAmpPageSeries = [];

      for (let j = 0; j < devicesSlice.length; j++) {
        minAmpPageSeries.push({
          x: devicesSlice[j].address.toString(),
          y: UtilsInstance.getFormatedAmplitudeForFaded(
            devicesSlice[j].amplitudeMin,
            devicesSlice[j].hasWaveProblem)
        });
      }

      // Complete the last slice with fake data, until it has exactly 15
      // bars to show on the screen
      if (i === (numOfPages - 1)) {
        let remainingDevices = Constants.MAX_DEV_PER_PAGE - minAmpPageSeries.length;

        if (remainingDevices > 0) {
          for (let j = 0; j < remainingDevices; j++) {
            minAmpPageSeries.push({ x: "", y: -1 });
          }
        }

      }

      ascAddrMinAmpMap.set(i, minAmpPageSeries);
    }

    return ascAddrMinAmpMap;
  }

  getNewest200Events = (log) => {
    let newest200Events = [];

    if (log.length > Constants.MAX_NUM_OF_LOG_EVENTS) {
      for (let i = 0; i < Constants.MAX_NUM_OF_LOG_EVENTS; i++) {
        newest200Events.push(log[i]);
      }
    }
    else {
      for (let i = 0; i < log.length; i++) {
        newest200Events.push(log[i]);
      }
    }

    return newest200Events;
  }

  // Função que faz um POST para avisar o backend que ele deverá começar a salvar as formas de 
  // onda na base de dados para que elas sejam utilizadas na hora de gerar o relatório
  startSaveWaves = async () => {
    let startSaveTime = await fetch(Constants.ANALYZER_REQUEST_IP_ADDRESS + "startSaveWaves_POST", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8"
      },
      body: { "update": true }
    })
      .then(UtilsInstance.checkStatus)
      .then(() => {
        console.log("startSaveWaves_POST funcionou");
        return Date.now();
      })
      .catch(() => {
        console.log("startSaveWaves_POST não funcionou");
        return Date.now();
      });

    return startSaveTime;
  };

  // ----------------- Lifecycle Methods ----------------
  componentDidMount() {
    this.isUserLoggedIn();
  }

  render() {
    const {
      clientProjectName,
      clientNetworkName,
      clientLocation,
      clientCompanyName,
      clientAccountableName,
      clientAddress,
      clientCity,
      clientTel,
      executingCompanyName,
      executingAccountableName,
      executingTel,
      executingEmail,
      notes
    } = this.state;

    return (
      <div style={divStyle}>

        <Grid columns={2} doubling stretched>
          <Grid.Column>
            <Segment>

              <Divider horizontal>{Strings.CLIENT_INFO}</Divider>
              <Form>
                <Form.Input label={Strings.PROJECT_NAME_LABEL} name='clientProjectName' value={clientProjectName} onChange={this.handleChange} />
                <Form.Input label={Strings.NETWORK_LABEL} name='clientNetworkName' value={clientNetworkName} onChange={this.handleChange} />
                <Form.Input label={Strings.LOCATION_LABEL} name='clientLocation' value={clientLocation} onChange={this.handleChange} />
                <Form.Input label={Strings.COMPANY_NAME_LABEL} name='clientCompanyName' value={clientCompanyName} onChange={this.handleChange} />
                <Form.Input label={Strings.ACCOUNTABLE_NAME_LABEL} name='clientAccountableName' value={clientAccountableName} onChange={this.handleChange} />
                <Form.Input label={Strings.ADDRESS} name='clientAddress' value={clientAddress} onChange={this.handleChange} />
                <Form.Input label={Strings.CITY_LABEL} name='clientCity' value={clientCity} onChange={this.handleChange} />
                <Form.Input label={Strings.TEL_LABEL} name='clientTel' value={clientTel} onChange={this.handleChange} />
                <Form.Input id="client-img" label={Strings.CLIENT_LOGO_LABEL} type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment>

              <Divider horizontal>{Strings.EXECUTING_COMPANY_INFO}</Divider>

              <Form>
                <Form.Input label={Strings.COMPANY_NAME_LABEL} name='executingCompanyName' value={executingCompanyName} onChange={this.handleChange} />
                <Form.Input label={Strings.ACCOUNTABLE_NAME_LABEL} name='executingAccountableName' value={executingAccountableName} onChange={this.handleChange} />
                <Form.Input label={Strings.TEL_LABEL} name='executingTel' value={executingTel} onChange={this.handleChange} />
                <Form.Input label={Strings.EMAIL_LABEL} name='executingEmail' value={executingEmail} onChange={this.handleChange} />
                <Form.Input id="executor-img" label={Strings.EXECUTING_COMPANY_LOGO_LABEL} type="file" accept=".png,.jpg,.jpeg" />
              </Form>
            </Segment>

            <Grid columns={2} doubling>
              <Grid.Column verticalAlign="middle">
                <Segment>
                  <Divider horizontal>{Strings.CONFORMITY}</Divider>
                  <Dropdown
                    fluid
                    selection
                    placeholder={Strings.DROPDOWN_PLACEHOLDER}
                    options={dropdownOptions}
                    onChange={this.handleConformityDropdownItemClick} />
                </Segment>
              </Grid.Column>

              <Grid.Column>
                <Segment>
                  <Divider horizontal>{Strings.REPORT_TYPE}</Divider>

                  <Form>
                    <Form.Field style={{ marginBottom: "-1vh", textAlign: "center" }}>
                      <Checkbox
                        label={Strings.PHYS_MEDIUM}
                        onChange={this.toggleDPCheckbox}
                        checked={this.state.isDPChecked} />
                    </Form.Field>
                    <Form.Field style={{ marginBottom: "-1vh", marginLeft: "-19pt" }}>
                      <Checkbox
                        label={Strings.MONITORING}
                        onChange={this.toggleMonitoringCheckbox}
                        checked={this.state.isMonChecked} />
                    </Form.Field>

                  </Form>

                </Segment>
              </Grid.Column>
            </Grid>

            <Segment>
              <Divider horizontal>{Strings.ANNOTATIONS}</Divider>
              <Form>
                <Form.Input type="text" name='notes' value={notes} onChange={this.handleChange} />
              </Form>
            </Segment>
          </Grid.Column>
        </Grid>

        <Button
          style={buttonStyle}
          color="teal"
          onClick={this.handleSubmit}>
          {Strings.GENERATE_REPORT}
        </Button>

        <PDFDownloadLink
          style={pdfLinkStyle}
          document={this.state.report}
          fileName={this.state.reportFilename}>
          {({ loading }) => {
            if (loading) {
              return Strings.FINISHING;
            }
            else {
              if (this.state.isGenerateReportDimmerActive) {
                this.setGeneratingDimmerState(false);
              }
              return Strings.SAVE_REPORT;
            }
          }}
        </PDFDownloadLink>

        <div id="chart" style={chartsStyle}></div>
        <div id="barchart" style={chartsStyle}></div>

        <Dimmer page active={this.state.isGenerateReportDimmerActive}>
          <Loader size="huge" indeterminate>
            {this.state.loaderText1}<br />
            {this.state.loaderText2}
          </Loader>
        </Dimmer>

      </div>
    );
  }
}

const chartsStyle = {
  width: "1026px",
  height: "580px",
  display: "none",
  visibility: "hidden"
};

const divStyle = {
  margin: "2vh 2vw",
  textAlign: "center"
};

const buttonStyle = {
  margin: "2vh auto"
};

const pdfLinkStyle = {
  marginLeft: "2vw"
};

export default withRouter(Report);
